import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { ChatState, ChatContextType, ChatMessage } from '../../types/chat';
import netlifyChatService from '../../services/netlifyChatService';

// Track IPs that have already been challenged
const challengedIPs = new Set<string>();

// Get client IP (simplified - in production you'd get this from the server)
const getClientIP = (): string => {
  // For now, use a combination of user agent and timestamp as a simple identifier
  // In production, this should be replaced with actual IP detection
  return `${navigator.userAgent}-${Math.floor(Date.now() / (1000 * 60 * 60))}`; // Changes every hour
};

// Initial state
const initialState: ChatState = {
  messages: [],
  isOpen: false,
  isLoading: false,
  error: null,
  hubSpotRedirect: null,
  challenge: null,
  pendingMessage: null, // Store the original message while challenge is active
};

// Action types
type ChatAction =
  | { type: 'SEND_MESSAGE'; payload: string }
  | { type: 'RECEIVE_MESSAGE'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'TOGGLE_CHAT' }
  | { type: 'CLEAR_CHAT' }
  | { type: 'SET_IS_OPEN'; payload: boolean }
  | { type: 'SET_HUBSPOT_REDIRECT'; payload: { message: string; retryAfter?: number } }
  | { type: 'SET_CHALLENGE'; payload: { question: string; answer: string } }
  | { type: 'CLEAR_CHALLENGE' }
  | { type: 'SET_PENDING_MESSAGE'; payload: string }
  | { type: 'CLEAR_PENDING_MESSAGE' }
  | { type: 'MARK_IP_CHALLENGED'; payload: string };

// Comprehensive challenge questions for client-side validation
// Using a subset of the full challenge system for client-side validation
const CLIENT_CHALLENGE_QUESTIONS = [
  // Math questions
  { question: "What is 2 + 3?", answer: "5" },
  { question: "What is 7 + 8?", answer: "15" },
  { question: "What is 12 - 5?", answer: "7" },
  { question: "What is 4 x 6?", answer: "24" },
  { question: "What is 15 ÷ 3?", answer: "5" },
  
  // Animal questions
  { question: "What animal says meow?", answer: "cat" },
  { question: "What animal says woof?", answer: "dog" },
  { question: "What animal says moo?", answer: "cow" },
  { question: "What animal says quack?", answer: "duck" },
  { question: "What animal says baa?", answer: "sheep" },
  
  // Color questions
  { question: "What color is the sky on a clear day?", answer: "blue" },
  { question: "What color is grass?", answer: "green" },
  { question: "What color is the sun?", answer: "yellow" },
  { question: "What color is snow?", answer: "white" },
  { question: "What color is a stop sign?", answer: "red" },
  
  // Logic questions
  { question: "If you have 3 apples and eat 1, how many do you have left?", answer: "2" },
  { question: "If you have 5 cookies and give 2 away, how many do you have?", answer: "3" },
  { question: "If you have 4 books and buy 3 more, how many do you have?", answer: "7" },
  { question: "If you have 10 dollars and spend 4, how much do you have left?", answer: "6" },
  { question: "If you have 6 pencils and lose 2, how many do you have?", answer: "4" },
  
  // Object questions
  { question: "What do you use to write on paper?", answer: "pen" },
  { question: "What do you use to cut paper?", answer: "scissors" },
  { question: "What do you use to tell time?", answer: "clock" },
  { question: "What do you use to open a door?", answer: "key" },
  { question: "What do you use to clean your teeth?", answer: "toothbrush" }
];

// Generate a random challenge
const generateChallenge = () => {
  const randomIndex = Math.floor(Math.random() * CLIENT_CHALLENGE_QUESTIONS.length);
  return CLIENT_CHALLENGE_QUESTIONS[randomIndex];
};

// Validate challenge answer locally
const validateChallengeAnswer = (userAnswer: string, correctAnswer: string): boolean => {
  return userAnswer.trim().toLowerCase() === correctAnswer.toLowerCase();
};

// Reducer function
const chatReducer = (state: ChatState, action: ChatAction): ChatState => {
  switch (action.type) {
    case 'SEND_MESSAGE':
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id: Date.now().toString(),
            content: action.payload,
            role: 'user',
            timestamp: new Date(),
          },
        ],
        isLoading: true,
        error: null,
      };

    case 'RECEIVE_MESSAGE':
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id: (Date.now() + 1).toString(),
            content: action.payload,
            role: 'assistant',
            timestamp: new Date(),
          },
        ],
        isLoading: false,
        error: null,
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case 'TOGGLE_CHAT':
      return {
        ...state,
        isOpen: !state.isOpen,
      };

    case 'SET_IS_OPEN':
      return {
        ...state,
        isOpen: action.payload,
      };

    case 'CLEAR_CHAT':
      return {
        ...state,
        messages: [],
        error: null,
        hubSpotRedirect: null,
        challenge: null,
        pendingMessage: null,
      };

    case 'SET_HUBSPOT_REDIRECT':
      return {
        ...state,
        hubSpotRedirect: action.payload,
      };

    case 'SET_CHALLENGE':
      return {
        ...state,
        challenge: action.payload,
      };

    case 'CLEAR_CHALLENGE':
      return {
        ...state,
        challenge: null,
      };

    case 'SET_PENDING_MESSAGE':
      return {
        ...state,
        pendingMessage: action.payload,
      };

    case 'CLEAR_PENDING_MESSAGE':
      return {
        ...state,
        pendingMessage: null,
      };

    case 'MARK_IP_CHALLENGED':
      // Mark this IP as challenged (stored in the Set, not in state)
      challengedIPs.add(action.payload);
      return state;

    default:
      return state;
  }
};

// Create context
const ChatContext = createContext<ChatContextType | undefined>(undefined);

// Provider component
export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, initialState);

  const sendMessage = async (message: string) => {
    try {
      // If there's an active challenge, validate the answer locally
      if (state.challenge) {
        const isValid = validateChallengeAnswer(message, state.challenge.answer);
        
        if (isValid) {
          // Clear the challenge
          dispatch({ type: 'CLEAR_CHALLENGE' });
          
          // Mark this IP as successfully challenged (in case it wasn't marked before)
          const clientIP = getClientIP();
          dispatch({ type: 'MARK_IP_CHALLENGED', payload: clientIP });
          
          // Send the original pending message to the server
          if (state.pendingMessage) {
            dispatch({ type: 'CLEAR_PENDING_MESSAGE' });
            
            // Add the original message to chat
            dispatch({ type: 'SEND_MESSAGE', payload: state.pendingMessage });
            
            // Send to server
            const response = await netlifyChatService.sendMessage(state.pendingMessage);
            
            // Handle HubSpot redirect
            if (response.redirectToHubSpot) {
              dispatch({ type: 'RECEIVE_MESSAGE', payload: response.message });
              dispatch({ 
                type: 'SET_HUBSPOT_REDIRECT', 
                payload: { 
                  message: response.hubSpotMessage || response.message, 
                  retryAfter: response.retryAfter 
                } 
              });
              return;
            }
            
            // Normal response
            dispatch({ type: 'RECEIVE_MESSAGE', payload: response.message });
          }
        } else {
          // Add error message for failed challenge
          dispatch({ 
            type: 'RECEIVE_MESSAGE', 
            payload: 'Please answer the verification question correctly.' 
          });
        }
        return;
      }

      // Get client IP for tracking
      const clientIP = getClientIP();
      
      // Check if this is the first message from this IP
      const isFirstMessageFromIP = !challengedIPs.has(clientIP);
      
      // Determine if we should trigger a challenge
      let shouldTriggerChallenge = false;
      
      if (isFirstMessageFromIP) {
        // Guarantee challenge for first message from new IP
        shouldTriggerChallenge = true;
        console.log('First message from new IP - triggering challenge');
      } else {
        // 20% chance for subsequent messages
        shouldTriggerChallenge = Math.random() < 0.2;
      }
      
      if (shouldTriggerChallenge) {
        const challenge = generateChallenge();
        
        // Store the original message
        dispatch({ type: 'SET_PENDING_MESSAGE', payload: message });
        
        // Add user message to chat
        dispatch({ type: 'SEND_MESSAGE', payload: message });
        
        // Set the challenge
        dispatch({ 
          type: 'SET_CHALLENGE', 
          payload: challenge 
        });
        
        // Mark this IP as challenged
        dispatch({ type: 'MARK_IP_CHALLENGED', payload: clientIP });
        
        // Add challenge message
        dispatch({ 
          type: 'RECEIVE_MESSAGE', 
          payload: `Please verify you are human by answering this question: ${challenge.question}` 
        });
        return;
      }

      // Normal message processing
      dispatch({ type: 'SEND_MESSAGE', payload: message });
      
      const response = await netlifyChatService.sendMessage(message);
      
      // Handle HubSpot redirect
      if (response.redirectToHubSpot) {
        dispatch({ type: 'RECEIVE_MESSAGE', payload: response.message });
        dispatch({ 
          type: 'SET_HUBSPOT_REDIRECT', 
          payload: { 
            message: response.hubSpotMessage || response.message, 
            retryAfter: response.retryAfter 
          } 
        });
        return;
      }

      // Handle server-side challenge verification (fallback)
      if (response.requiresVerification) {
        dispatch({ type: 'RECEIVE_MESSAGE', payload: response.message });
        dispatch({ 
          type: 'SET_CHALLENGE', 
          payload: { 
            question: response.challenge || '', 
            answer: response.challengeAnswer || '' 
          } 
        });
        return;
      }
      
      // Clear any existing challenge when message is processed normally
      dispatch({ type: 'CLEAR_CHALLENGE' });
      dispatch({ type: 'RECEIVE_MESSAGE', payload: response.message });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
    }
  };

  const toggleChat = () => {
    dispatch({ type: 'TOGGLE_CHAT' });
  };

  const clearChat = () => {
    netlifyChatService.clearHistory();
    dispatch({ type: 'CLEAR_CHAT' });
  };

  const value: ChatContextType = {
    state,
    sendMessage,
    toggleChat,
    clearChat,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

// Custom hook to use the chat context
export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}; 